import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EyeIcon, PencilIcon, PlusIcon, PrinterIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { HsePageChrome } from "../../../../../components/dashboard/hse/HsePageChrome";
import { ModuleFilter } from "../../../../../components/utils/ModuleFilter";
import { ResourceEmpty } from "../../../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { useUserHasAccess } from "../../../../../routes/roleOnly";
import { exportSjaPdf, fetchSjas } from "../../../../../api/hse/risk";
import { HseRowActionsMenu } from "../../../../../components/dashboard/hse/HseRowActionsMenu";
import { RiskLevelBadge } from "../../../../../components/dashboard/hse/risk/RiskLevelBadge";
import { RiskSubnav } from "../../../../../components/dashboard/hse/risk/RiskSubnav";
import { HseListCard, HsePageBody } from "../../../../../components/dashboard/hse/HsePageBody";
import { HseGuideButton, HseModuleGuide } from "../../../../../components/dashboard/hse/HseModuleGuide";
import {
  formatDisplayDate,
  riskApiError,
  SJA_STATUSES,
  SJA_WORK_TYPES,
} from "../../../../../components/dashboard/hse/risk/riskUtils";

export const HseKsSjaList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useUserHasAccess({ role: "hse_ks_risk", action: "create" });
  const canEdit = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ query: "", sja_statuses: [], sja_types: [] });
  const [guideOpen, setGuideOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchSjas({
        query: filters.query,
        status: filters.sja_statuses?.[0],
        work_type: filters.sja_types?.[0],
        per_page: 50,
      });
      setItems(response.data || []);
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.sja.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query, filters.sja_statuses, filters.sja_types]);

  const handleExport = async (item) => {
    try {
      await exportSjaPdf(item.id, `hse-sja-${item.id}.pdf`);
      toast.success(t("hse.risk.pdf_ready"));
    } catch (err) {
      toast.error(err.message || t("hse.risk.pdf_failed"));
    }
  };

  return (
    <HsePageChrome
      variant="list"
      title={t("hse.risk.sja.title")}
      subtitle={t("hse.risk.sja.subtitle")}
      listCount={loading ? null : items.length}
      mainButton={
        canCreate
          ? { label: t("hse.risk.sja.new"), icon: PlusIcon, path: "/dashboard/hse-ks/risk/sja/new" }
          : undefined
      }
    >
      <HsePageBody>
        <RiskSubnav active="sja" />
        <div className="mb-4 flex justify-end">
          <HseGuideButton onClick={() => setGuideOpen(true)} />
        </div>
        <HseModuleGuide topic="sja" open={guideOpen} onClose={() => setGuideOpen(false)} />
        <div>
          <ModuleFilter
            filterCard
            hideStatusFilter
            searchPlaceholder={t("hse.risk.search")}
            initialFilters={filters}
            onFiltersChange={(next) =>
              setFilters({
                query: next.query || "",
                sja_statuses: next.sja_statuses || [],
                sja_types: next.sja_types || [],
              })
            }
            customDropdownFilters={[
              {
                key: "sja_statuses",
                label: t("hse.risk.fields.status"),
                options: SJA_STATUSES.map((status) => ({
                  value: status,
                  label: t(`hse.risk.sja.status.${status}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
              {
                key: "sja_types",
                label: t("hse.risk.sja.work_type"),
                options: SJA_WORK_TYPES.map((type) => ({
                  value: type,
                  label: t(`hse.risk.sja.work_type.${type}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
            ]}
          />
        </div>
        <HseListCard>
          {loading ? (
            <div className="p-8">
              <ResourceLoading />
            </div>
          ) : error ? (
            <ResourceError errors={[error]} onRetry={load} />
          ) : items.length === 0 ? (
            <div className="px-6 py-16">
              <ResourceEmpty message={t("hse.risk.sja.empty")} />
              {canCreate ? (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/hse-ks/risk/sja/new")}
                    className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
                  >
                    {t("hse.risk.sja.new")}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50">
                  <Link to={`/dashboard/hse-ks/risk/sja/${item.id}`} className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900">{item.title}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {t(`hse.risk.sja.work_type.${item.work_type}`)}
                      {item.work_location ? ` · ${item.work_location}` : ""}
                      {item.updated_at ? ` · ${formatDisplayDate(item.updated_at)}` : ""}
                    </p>
                  </Link>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">
                      {t(`hse.risk.sja.status.${item.status}`)}
                    </span>
                    {item.overall_risk_level ? (
                      <RiskLevelBadge level={item.overall_risk_level} />
                    ) : null}
                    <HseRowActionsMenu
                      label={t("hse.risk.actions")}
                      items={[
                        {
                          label: t("hse.risk.open"),
                          icon: EyeIcon,
                          action: () => navigate(`/dashboard/hse-ks/risk/sja/${item.id}`),
                        },
                        {
                          label: t("hse.risk.edit"),
                          icon: PencilIcon,
                          hidden: !canEdit || (item.status && item.status !== "draft"),
                          action: () => navigate(`/dashboard/hse-ks/risk/sja/${item.id}/edit`),
                        },
                        {
                          label: t("hse.risk.pdf"),
                          icon: PrinterIcon,
                          action: () => handleExport(item),
                        },
                      ]}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </HseListCard>
      </HsePageBody>
    </HsePageChrome>
  );
};
